import type { TrackerAdapter } from '../types';
import {
  ERROR_EVENT_RUNTIME,
  ERROR_EVENT_PROMISE,
  ERROR_EVENT_RESOURCE,
  ERROR_MESSAGE_MAX_LEN,
  ERROR_STACK_MAX_LEN,
} from '../constants';
import { isBrowser } from '../utils';

/** 错误观察器配置项 */
export interface ErrorObserverOptions {
  reportError: boolean;
  reportResourceError: boolean;
  sampleRate: number;
  maxCount: number;
  dedupWindow: number;
  ignoreList: Array<string | RegExp>;
  debug?: boolean;
}

/** 手动错误上报 API，由 setupErrorObservers 返回 */
export interface ErrorReporter {
  /** 手动上报错误（不经过采样，但仍受去重 / 限频约束） */
  captureError: (error: Error | string, extra?: Record<string, any>) => void;
  /** 重置限频计数与去重缓存（SPA 路由切换时调用） */
  resetCount: () => void;
}

interface ErrorContext {
  deduper: { shouldDrop(signature: string): boolean; reset(): void };
  limiter: { canReport(): boolean; reset(): void };
  sampler: { hit(): boolean };
  ignoreList: Array<string | RegExp>;
  debug: boolean;
}

/** 安全截断字符串到指定长度 */
function truncate(text: string | undefined | null, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) : text;
}

/** 检查文本是否命中忽略规则（字符串子串匹配或正则匹配） */
function shouldIgnore(
  text: string,
  ignoreList: Array<string | RegExp>,
): boolean {
  if (!text || ignoreList.length === 0) return false;
  for (const rule of ignoreList) {
    try {
      if (typeof rule === 'string') {
        if (text.indexOf(rule) !== -1) return true;
      } else if (rule && typeof rule.test === 'function' && rule.test(text)) {
        return true;
      }
    } catch {
      // 忽略格式异常的规则
    }
  }
  return false;
}

/** 创建去重器：在时间窗口内丢弃相同签名的错误 */
function createDeduper(dedupWindow: number) {
  const seen = new Map<string, number>();
  return {
    shouldDrop(signature: string): boolean {
      const now = Date.now();
      // 清理过期条目以控制内存占用
      if (seen.size > 50) {
        for (const [key, ts] of seen) {
          if (now - ts > dedupWindow) seen.delete(key);
        }
      }
      const last = seen.get(signature);
      if (last !== undefined && now - last < dedupWindow) {
        return true;
      }
      seen.set(signature, now);
      return false;
    },
    reset(): void {
      seen.clear();
    },
  };
}

/** 创建限频器：最多允许 maxCount 次上报 */
function createRateLimiter(maxCount: number) {
  let count = 0;
  return {
    canReport(): boolean {
      if (count >= maxCount) return false;
      count++;
      return true;
    },
    reset(): void {
      count = 0;
    },
  };
}

/** 创建采样器：以 rate 的概率返回 true */
function createSampler(rate: number) {
  const r = Math.max(0, Math.min(1, rate));
  return {
    hit(): boolean {
      if (r >= 1) return true;
      if (r <= 0) return false;
      return Math.random() < r;
    },
  };
}

function logDebug(debug: boolean, message: string, data?: any): void {
  if (!debug) return;
  if (data !== undefined) console.info(`[yl-nsm-track] ${message}`, data);
  else console.info(`[yl-nsm-track] ${message}`);
}

/**
 * 上报核心函数 —— 依次执行忽略 / 去重 / 限频 / 采样，通过后发送事件。
 * adapter.event 内部已合并 getDefaultProps，这里只传业务 payload。
 */
function report(
  adapter: TrackerAdapter,
  ctx: ErrorContext,
  eventName: string,
  payload: Record<string, any>,
  signature: string,
  ignoreText: string,
  withSampling: boolean,
): void {
  if (shouldIgnore(ignoreText, ctx.ignoreList)) {
    logDebug(ctx.debug, 'Error ignored by rule.', { signature });
    return;
  }
  if (ctx.deduper.shouldDrop(signature)) {
    logDebug(ctx.debug, 'Error deduplicated.', { signature });
    return;
  }
  if (!ctx.limiter.canReport()) {
    logDebug(ctx.debug, 'Error rate limit reached, dropping.', { signature });
    return;
  }
  let sampled = true;
  if (withSampling) {
    sampled = ctx.sampler.hit();
    if (!sampled) {
      logDebug(ctx.debug, 'Error sampled out.', { signature });
      return;
    }
  }
  adapter.event(eventName, {
    ...payload,
    error_sampled: sampled,
  });
}

/** 注册 window 'error' 监听（捕获阶段），同时处理运行时错误与资源错误 */
function installGlobalErrorHandler(
  adapter: TrackerAdapter,
  ctx: ErrorContext,
  options: ErrorObserverOptions,
): void {
  if (!isBrowser()) return;

  const handler = (event: ErrorEvent): void => {
    const target = event.target as any;
    const isResourceTarget =
      target != null &&
      target !== window &&
      target !== document &&
      typeof target.tagName === 'string';

    // 资源加载错误（img / script / link / ...）
    if (isResourceTarget) {
      if (!options.reportResourceError) return;
      const tagName = target.tagName as string;
      const source = String(target.src || target.href || '');
      const message = `Failed to load ${tagName.toLowerCase()}: ${source}`;
      const signature = `resource|${tagName}|${source}`;
      report(
        adapter,
        ctx,
        ERROR_EVENT_RESOURCE,
        {
          error_type: 'resource',
          error_message: truncate(message, ERROR_MESSAGE_MAX_LEN),
          error_source: truncate(source, ERROR_MESSAGE_MAX_LEN),
          error_tag: tagName,
          error_client_ts: Date.now(),
        },
        signature,
        `${message} ${source}`,
        true,
      );
      return;
    }

    // JS 运行时错误
    if (!options.reportError) return;
    const message = event.message || 'Script error';
    const filename = event.filename || '';
    const lineno = event.lineno || 0;
    const colno = event.colno || 0;
    const stack =
      event.error && event.error.stack ? String(event.error.stack) : '';
    const signature = `runtime|${message}|${filename}|${lineno}|${colno}`;
    report(
      adapter,
      ctx,
      ERROR_EVENT_RUNTIME,
      {
        error_type: 'runtime',
        error_message: truncate(message, ERROR_MESSAGE_MAX_LEN),
        error_stack: truncate(stack, ERROR_STACK_MAX_LEN),
        error_filename: truncate(filename, ERROR_MESSAGE_MAX_LEN),
        error_lineno: lineno,
        error_colno: colno,
        error_client_ts: Date.now(),
      },
      signature,
      `${message} ${filename}`,
      true,
    );
  };

  // 使用捕获阶段，以便同时捕获不冒泡的资源错误
  window.addEventListener('error', handler, true);
}

/** 注册 window 'unhandledrejection' 监听 */
function installUnhandledRejectionHandler(
  adapter: TrackerAdapter,
  ctx: ErrorContext,
): void {
  if (!isBrowser()) return;

  const handler = (event: PromiseRejectionEvent): void => {
    const reason: any = event.reason;
    let message: string;
    let stack: string;

    if (reason instanceof Error) {
      message = reason.message || 'Unknown error';
      stack = reason.stack || '';
    } else if (typeof reason === 'string') {
      message = reason;
      stack = '';
    } else {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = String(reason);
      }
      stack = '';
    }

    const signature = `promise|${message}`;
    report(
      adapter,
      ctx,
      ERROR_EVENT_PROMISE,
      {
        error_type: 'promise',
        error_message: truncate(message, ERROR_MESSAGE_MAX_LEN),
        error_stack: truncate(stack, ERROR_STACK_MAX_LEN),
        error_reason: truncate(message, ERROR_MESSAGE_MAX_LEN),
        error_client_ts: Date.now(),
      },
      signature,
      message,
      true,
    );
  };

  window.addEventListener('unhandledrejection', handler);
}

/**
 * 根据 init 配置注册错误观察器。
 * 返回 ErrorReporter，用于手动上报与路由切换时的计数重置。
 */
export function setupErrorObservers(
  adapter: TrackerAdapter,
  options: ErrorObserverOptions,
): ErrorReporter {
  const ctx: ErrorContext = {
    deduper: createDeduper(options.dedupWindow),
    limiter: createRateLimiter(options.maxCount),
    sampler: createSampler(options.sampleRate),
    ignoreList: options.ignoreList,
    debug: options.debug === true,
  };

  if (isBrowser()) {
    if (options.reportError || options.reportResourceError) {
      installGlobalErrorHandler(adapter, ctx, options);
    }
    if (options.reportError) {
      installUnhandledRejectionHandler(adapter, ctx);
    }
  }

  return {
    captureError(error: Error | string, extra?: Record<string, any>): void {
      const message =
        typeof error === 'string' ? error : error.message || 'Unknown error';
      const stack =
        typeof error === 'string'
          ? ''
          : (error.stack && String(error.stack)) || '';
      const signature = `manual|${message}|${stack.slice(0, 200)}`;
      report(
        adapter,
        ctx,
        ERROR_EVENT_RUNTIME,
        {
          error_type: 'runtime',
          error_message: truncate(message, ERROR_MESSAGE_MAX_LEN),
          error_stack: truncate(stack, ERROR_STACK_MAX_LEN),
          error_client_ts: Date.now(),
          ...(extra || {}),
        },
        signature,
        message,
        false, // 手动上报不经过采样
      );
    },
    resetCount(): void {
      ctx.limiter.reset();
      ctx.deduper.reset();
    },
  };
}
